/**
 * Tenant Overview Page
 * @description Tenant 개요 - 기본 정보, 통계, 빠른 액세스
 */

import { useEffect, useState } from 'react';
import './tenant-admin.css';
import { Badge, Card } from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import {
    Users,
    FileText,
    CheckCircle,
    Settings,
    BarChart3,
    PieChart
} from 'lucide-react';

interface TenantStats {
    userCount: number;
    ticketCount: number;
    activeWorkflows: number;
    activeConnectors: number;
    ticketsByType: Record<string, number>;
    ticketsByStatus: Record<string, number>;
}

interface TenantInfo {
    tenantId: string;
    tenantName: string;
    status: string;
    generatedAt: string;
}

export const TenantOverviewPage: React.FC = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(true);
    const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
    const [stats, setStats] = useState<TenantStats | null>(null);

    useEffect(() => {
        const currentUser = user;
        if (currentUser?.userType === 'tenant-user') {
            loadData(currentUser.tenantId);
        }
    }, [user]);

    const loadData = async (tenantId: string) => {
        try {
            setLoading(true);

            // 실제 API 호출
            const data = await dataService.getTenantOverview(tenantId);

            setTenantInfo({
                tenantId: data.tenantId || tenantId,
                tenantName: data.tenantName || 'GS리테일',
                status: data.status || 'active',
                generatedAt: data.generatedAt || '2025-01-01T00:00:00Z'
            });

            setStats({
                userCount: data.stats?.userCount ?? 0,
                ticketCount: data.stats?.ticketCount ?? 0,
                activeWorkflows: data.stats?.activeWorkflows ?? 0,
                activeConnectors: data.stats?.activeConnectors ?? 0,
                ticketsByType: data.stats?.ticketsByType ?? {},
                ticketsByStatus: data.stats?.ticketsByStatus ?? {}
            });
        } catch (error) {
            console.error('데이터를 불러오는데 실패했습니다:', error);

            // 에러 시 기본 데이터 사용
            setTenantInfo({
                tenantId: tenantId,
                tenantName: 'GS리테일',
                status: 'active',
                generatedAt: '2025-01-01T00:00:00Z'
            });

            setStats({
                userCount: 0,
                ticketCount: 0,
                activeWorkflows: 0,
                activeConnectors: 0,
                ticketsByType: {},
                ticketsByStatus: {}
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!user || user.userType !== 'tenant-user') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-gray-500">테넌트 사용자로 로그인해주세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tenant-admin-page space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">
                    테넌트 개요: {tenantInfo?.tenantName || tenantInfo?.tenantId}
                </h1>
                <p className="text-gray-600 mt-1">
                    테넌트 기본 정보 및 사용 현황을 확인할 수 있습니다.
                </p>
            </div>

            {/* 기본 정보 */}
            <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">기본 정보</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <span className="text-sm text-gray-500">Tenant ID</span>
                        <p className="font-medium">{tenantInfo?.tenantId}</p>
                    </div>
                    <div>
                        <span className="text-sm text-gray-500">Tenant Name</span>
                        <p className="font-medium">{tenantInfo?.tenantName}</p>
                    </div>
                    <div>
                        <span className="text-sm text-gray-500">상태</span>
                        <div className="mt-1">
                            <Badge variant={tenantInfo?.status === 'active' ? 'default' : 'secondary'}>
                                {tenantInfo?.status === 'active' ? '활성' : '비활성'}
                            </Badge>
                        </div>
                    </div>
                    <div>
                        <span className="text-sm text-gray-500">생성일</span>
                        <p className="font-medium">
                            {tenantInfo?.generatedAt
                                ? new Date(tenantInfo.generatedAt).toLocaleDateString('ko-KR')
                                : '-'
                            }
                        </p>
                    </div>
                </div>
            </Card>

            {/* 통계 카드 */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-500">사용자 수</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.userCount}</p>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <FileText className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-500">티켓 수</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.ticketCount}</p>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <CheckCircle className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-500">활성 워크플로우</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.activeWorkflows}</p>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Settings className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm text-gray-500">활성 커넥터</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.activeConnectors}</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* 차트 영역 */}
            {stats && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                            <PieChart className="h-5 w-5 mr-2" />
                            티켓 타입별 분포
                        </h3>
                        <div className="space-y-3">
                            {Object.entries(stats.ticketsByType).map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{type}</span>
                                    <div className="flex items-center">
                                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full"
                                                style={{
                                                    width: `${stats.ticketCount > 0 ? (count / stats.ticketCount) * 100 : 0}%`
                                                }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-medium w-8 text-right">{count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                            <BarChart3 className="h-5 w-5 mr-2" />
                            티켓 상태별 분포
                        </h3>
                        <div className="space-y-3">
                            {Object.entries(stats.ticketsByStatus).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{status}</span>
                                    <div className="flex items-center">
                                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                                            <div
                                                className="bg-green-600 h-2 rounded-full"
                                                style={{
                                                    width: `${stats.ticketCount > 0 ? (count / stats.ticketCount) * 100 : 0}%`
                                                }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-medium w-8 text-right">{count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

TenantOverviewPage.displayName = 'TenantOverviewPage';